import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, ArrowBack, Close, ContentCopy, Delete, Edit, SaveOutlined } from "@mui/icons-material";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import RichTextEditor from "../../../components/ui/RichTextEditor";
import apiService from "../../../service/Api";
import type { ShareTemplate } from "../share-messages/types/share-template.types";
import {
  DEFAULT_INTERNET_AGREEMENT,
  ensureAgreementCheckboxes,
  isLegacyInternetAgreement,
  removeAgreementPageBreaks,
} from "./defaultInternetAgreement";
import internetAgreementLogo from "../../../assets/internetAgreementLogo.png";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";
import {
  formatAgreementAuditDate,
  htmlToReadableText,
} from "./internetAgreementDisplay";

const PLACEHOLDERS = [
  "eventName",
  "clientName",
  "clientEmail",
  "clientPhone",
  "bookingReference",
  "deceasedName",
  "deceasedFirstName",
  "deceasedSurname",
  "serviceDate",
  "serviceTime",
  "serviceVenue",
  "serviceAddress",
  "streamLink",
  "youtubeLink",
  "vimeoLink",
];
const AGREEMENT_TITLE =
  "Livestream Booking Agreement & Client Acknowledgement Form";
const PREVIEW_VALUES: Record<string, string> = {
  eventName: "Celebration of Life Livestream",
  clientName: "Jane Smith",
  clientEmail: "jane.smith@example.com",
  clientPhone: "+27 71 234 5678",
  bookingReference: "BK-1024",
  deceasedName: "John Smith",
  deceasedFirstName: "John",
  deceasedSurname: "Smith",
  serviceDate: "Thursday 12 August 2026",
  serviceTime: "10:00",
  serviceVenue: "Muzukuru Memorial Chapel",
  serviceAddress: "12 Memorial Road, Johannesburg",
  streamLink: "https://example.com/stream",
  youtubeLink: "https://youtube.com/watch/example",
  vimeoLink: "https://vimeo.com/example",
};

const withPreviewValues = (html: string) =>
  html.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (match, key: string) =>
    PREVIEW_VALUES[key] ?? match,
  );

const InternetAgreementConfiguration = () => {
  const { hasPermission } = usePermissions();
  const [template, setTemplate] = useState<ShareTemplate | null>(null);
  const [templates, setTemplates] = useState<ShareTemplate[]>([]);
  const [templateName, setTemplateName] = useState(AGREEMENT_TITLE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(
    DEFAULT_INTERNET_AGREEMENT,
  );
  const [saving, setSaving] = useState(false);
  const [fullScreenPreviewOpen, setFullScreenPreviewOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadTemplates = async () => {
    const { data } = await apiService.get<{ results: ShareTemplate[] }>(
      "/api/configurations/message-templates/internet-agreements/",
    );
    setTemplates(data.results);
    return data.results;
  };

  useEffect(() => {
    apiService
      .get<{ results: ShareTemplate[] }>(
        "/api/configurations/message-templates/internet-agreements/",
      )
      .then(({ data }) => {
        const loaded = data.results[0] ?? null;
        setTemplates(data.results);
        const content =
          loaded?.body_template &&
          !isLegacyInternetAgreement(loaded.body_template)
            ? ensureAgreementCheckboxes(
                removeAgreementPageBreaks(loaded.body_template),
              )
            : DEFAULT_INTERNET_AGREEMENT;
        setTemplate(loaded);
        setPreviewContent(content);
      })
      .catch(() =>
        setError("Could not load the livestream booking agreement configuration."),
      )
      .finally(() => setLoading(false));
  }, []);

  const editorContent = useMemo(
    () =>
      template?.body_template &&
      !isLegacyInternetAgreement(template.body_template)
        ? ensureAgreementCheckboxes(
            removeAgreementPageBreaks(template.body_template),
          )
        : DEFAULT_INTERNET_AGREEMENT,
    [template],
  );

  const save = async (bodyTemplate: string) => {
    setSaving(true);
    try {
      const payload = {
        name: templateName.trim(),
        category: template?.category || "other",
        template_type: "internet_agreement" as const,
        body_template: ensureAgreementCheckboxes(
          removeAgreementPageBreaks(bodyTemplate),
        ),
      };
      const response = template
        ? await apiService.put<ShareTemplate>(
            `/api/configurations/message-templates/${template.id}/`,
            payload,
          )
        : await apiService.post<ShareTemplate>(
            "/api/configurations/message-templates/",
            payload,
          );
      setTemplate(response.data);
      await loadTemplates();
      setPreviewContent(response.data.body_template);
      setEditOpen(false);
      setFullScreenPreviewOpen(true);
      toast.success("Livestream booking agreement saved");
    } finally {
      setSaving(false);
    }
  };

  const deleteAgreement = async () => {
    if (!template) return;
    setDeleting(true);
    try {
      await apiService.delete(
        `/api/configurations/message-templates/${template.id}/`,
      );
      const remaining = await loadTemplates();
      setTemplate(remaining[0] ?? null);
      setPreviewContent(remaining[0]?.body_template ?? DEFAULT_INTERNET_AGREEMENT);
      setDeleteConfirmOpen(false);
      toast.success("Livestream booking agreement deleted");
    } finally {
      setDeleting(false);
    }
  };

  const duplicateAgreement = async (source: ShareTemplate) => {
    try {
      const { data } = await apiService.post<ShareTemplate>(
        `/api/configurations/message-templates/${source.id}/duplicate-internet-agreement/`,
        {},
      );
      await loadTemplates();
      toast.success(`${data.name} created`);
    } catch {
      toast.error("Could not duplicate the agreement");
    }
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box
      sx={editOpen ? {
        height: "calc(100dvh - 120px)",
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      } : undefined}
    >
      {!editOpen && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 1,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {AGREEMENT_TITLE}
            </Typography>
            {hasPermission(PERMISSIONS.CONFIGURATION_ADD) && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setTemplate(null);
                  setTemplateName("");
                  setPreviewContent("");
                  setEditOpen(true);
                }}
              >
                Add Agreement
              </Button>
            )}
          </Box>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Manage the agreement displayed for streams.
          </Typography>
        </>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!editOpen && templates.length ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 430px))" }, gap: 2 }}>
        {templates.map((item) => {
          const itemContent = item.body_template && !isLegacyInternetAgreement(item.body_template)
            ? ensureAgreementCheckboxes(removeAgreementPageBreaks(item.body_template))
            : DEFAULT_INTERNET_AGREEMENT;
          return <Card
          key={item.id}
          sx={{
            maxWidth: 430,
            minHeight: 190,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ flex: 1 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="h6">
                {item.name}
              </Typography>
              <Chip label={item.is_default_template ? "Default" : "Agreement"} size="small" color="primary" />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {htmlToReadableText(itemContent)}
            </Typography>
            <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography variant="caption" display="block" color="text.secondary">
                Created by {item.created_by_name || "System"} · {formatAgreementAuditDate(item.created_at)}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                Last updated by {item.last_updated_by_name || item.created_by_name || "System"} · {formatAgreementAuditDate(item.updated_at)}
              </Typography>
            </Box>
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end" }}>
            {hasPermission(PERMISSIONS.CONFIGURATION_CHANGE) && (
              <IconButton
                aria-label="Edit livestream booking agreement"
                onClick={() => {
                  setTemplate(item);
                  setTemplateName(item.name);
                  setPreviewContent(itemContent);
                  setEditOpen(true);
                }}
              >
                <Edit />
              </IconButton>
            )}
            {hasPermission(PERMISSIONS.CONFIGURATION_ADD) && (
              <Tooltip title="Duplicate template" arrow>
                <IconButton aria-label={`Duplicate ${item.name}`} onClick={() => duplicateAgreement(item)}>
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            )}
            {!item.is_default_template && hasPermission(PERMISSIONS.CONFIGURATION_DELETE) && (
              <IconButton
                aria-label="Delete livestream booking agreement"
                color="error"
                onClick={() => {
                  setTemplate(item);
                  setDeleteConfirmOpen(true);
                }}
              >
                <Delete />
              </IconButton>
            )}
          </CardActions>
        </Card>;
        })}
        </Box>
      ) : !editOpen ? (
        <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
          No livestream booking agreement has been added yet.
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                aria-label="Back to template"
                onClick={() => setEditOpen(false)}
                sx={{ mr: 1 }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" fontWeight={700}>
                {template ? `Edit ${template.name}` : "Add Agreement Template"}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<SaveOutlined />}
              disabled={saving || !templateName.trim()}
              onClick={() => save(previewContent)}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </Box>

          <TextField
            label="Template name"
            value={templateName}
            onChange={(event) => setTemplateName(event.target.value)}
            size="small"
            required
            sx={{ mb: 1.5, maxWidth: 560, flexShrink: 0 }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
              gap: 2,
              alignItems: "stretch",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                border: "0.5px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: 38,
                  px: 2,
                  borderBottom: "0.5px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={500}
                  color="text.secondary"
                >
                  Editor
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  "& .MuiPaper-root": {
                    boxShadow: "none",
                    borderRadius: 0,
                    p: 0,
                    height: "100%",
                  },
                }}
              >
                <RichTextEditor
                  key={template?.id ?? "new"}
                  title=""
                  initialContent={template ? editorContent : ""}
                  onSave={save}
                  onChange={setPreviewContent}
                  placeholders={PLACEHOLDERS}
                  showSaveButton={false}
                  placeholderDisplay="dropdown"
                  toolbarPreset="compact"
                  contentHeight="fill"
                />
              </Box>
            </Box>

            <Box
              sx={{
                minWidth: 0,
                border: "0.5px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: 38,
                  px: 2,
                  borderBottom: "0.5px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={500}
                  color="text.secondary"
                >
                  Live preview
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Updates as you type
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  bgcolor: "#f0f0f0",
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  overflow: "auto",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#fff",
                    color: "#000",
                    width: "100%",
                    maxWidth: 480,
                    aspectRatio: "8.5 / 11",
                    p: "6% 7%",
                    fontFamily: '"Montserrat", Arial, sans-serif',
                    boxShadow: 1,
                    overflow: "auto",
                  }}
                >
                  {previewContent.trim() && (
                    <Box
                      component="img"
                      src={internetAgreementLogo}
                      alt="Muzukuru"
                      sx={{
                        width: "60%",
                        maxWidth: 180,
                        height: "auto",
                        objectFit: "contain",
                        mb: 2,
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.45,
                      "& h1": { fontSize: 18, fontWeight: 500 },
                      "& h2": { fontSize: 20, mt: 2.5, mb: 1 },
                      "& h3": { fontSize: 15, mt: 1.5, mb: 1 },
                      "& .agreement-field": {
                        display: "inline-block",
                        minWidth: 140,
                        borderBottom: "1px solid #000",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        withPreviewValues(removeAgreementPageBreaks(previewContent)),
                      ),
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Dialog
        fullScreen
        open={fullScreenPreviewOpen}
        onClose={() => setFullScreenPreviewOpen(false)}
      >
        <DialogTitle
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setFullScreenPreviewOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Back to Agreements
            </Button>
            <Typography variant="h6" fontWeight={600}>
              Updated Preview
            </Typography>
          </Box>
          <IconButton
            aria-label="Close full-screen preview"
            title="Close preview"
            onClick={() => setFullScreenPreviewOpen(false)}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#eceff3", py: 3 }}>
          <Box
            sx={{
              bgcolor: "#fff",
              color: "#000",
              width: "8.5in",
              maxWidth: "100%",
              minHeight: "11in",
              mx: "auto",
              p: "0.7in",
              boxSizing: "border-box",
              fontFamily: '"Montserrat", Arial, sans-serif',
              boxShadow: 2,
            }}
          >
            {previewContent.trim() && (
              <Box
                component="img"
                src={internetAgreementLogo}
                alt="Muzukuru"
                sx={{ width: 220, maxWidth: "60%", mb: 3 }}
              />
            )}
            <Box
              sx={{
                fontSize: 14,
                lineHeight: 1.55,
                "& h1": { fontSize: 24 },
                "& h2": { fontSize: 22, mt: 3, mb: 1.5 },
                "& h3": { fontSize: 17, mt: 2, mb: 1 },
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  withPreviewValues(removeAgreementPageBreaks(previewContent)),
                ),
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete agreement?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete the configured {AGREEMENT_TITLE}. This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={deleteAgreement}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InternetAgreementConfiguration;
